import DynamicFieldsFragment from '@linc-technologies/linced-common/models/dynamic-field/base-fragment';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr } from '@warp-drive/legacy/model';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import {
  array,
  fragment,
  fragmentArray,
} from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type EnrolmentStatus from '@linc-technologies/linced-common/enums/enrolment-status';
import type Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import type PersonType from '@linc-technologies/linced-common/enums/person-type';
import type BulkImportContact from 'linced-app/models/people/bulk-import-contact';
import type BulkImportMedicalContact from 'linced-app/models/people/bulk-import-medical-contact';
import type EnrolmentMeta from 'linced-app/models/people/enrolment-meta';
import type MedicalCondition from 'linced-app/models/people/medical-condition';

// Proto: people.BulkImportPerson.
export default class BulkImportPerson extends DynamicFieldsFragment {
  declare [Type]: 'people/bulk-import-person';

  // type is the record type of the Person.
  @attr('number', { defaultValue: 0 })
  declare type: EnumValue<typeof PersonType> | 0;

  // personId is the existing Hero Person ID, if known / applicable.
  @attr('string', { defaultValue: '' })
  declare personId: string;

  // jurisdiction specifies the educational jurisdiction the Person should be
  // considered under, if the School is multi-jurisdictional.
  @attr('number', { defaultValue: 0 })
  declare jurisdiction: EnumValue<typeof Jurisdiction> | 0;

  // fields holds this Person's dynamic field values to be imported.
  // DynamicFieldsFragment holds them.
  // contacts holds a list of this Person's Contacts (for Learners only).
  @fragmentArray('people/bulk-import-contact')
  declare contacts: FragmentArray<BulkImportContact>;

  // medicalContacts holds a list of this Person's Medical Contacts
  // (for Learners only).
  @fragmentArray('people/bulk-import-medical-contact')
  declare medicalContacts: FragmentArray<BulkImportMedicalContact>;

  // medicalConditions holds a list of this Person's Medical Conditions
  // (for Learners only).
  @fragmentArray('people/medical-condition')
  declare medicalConditions: FragmentArray<MedicalCondition>;

  // status specifies a learner's current enrolment status.
  @attr('number', { defaultValue: 0 })
  declare status: EnumValue<typeof EnrolmentStatus> | 0;

  // meta contains extra enrolment information that Hero can inject
  // mainly based on jurisdictional differences.
  @fragment('people/enrolment-meta')
  declare meta: EnrolmentMeta | null;

  // fte is the Full Time Equivalent the Learner is taking.
  // No default: 0 FTE is a real value, so undefined means not given.
  @attr('number')
  declare fte: number | undefined;

  // entryDate is when the Learner started this school. Format: ISO 8601.
  @attr('string')
  declare entryDate: string | undefined;

  // exitDate is when the Learner left this school. Format: ISO 8601.
  @attr('string')
  declare exitDate: string | undefined;

  // departureReason is the reason the Learner left this school.
  @attr('string', { defaultValue: '' })
  declare departureReason: string;

  // enrolmentPropsToImport specifies which enrolment properties to import.
  // This is to work around the ambiguity of the empty value (are we
  // importing an empty value, or do we want to leave it unchanged?).
  @array('number')
  declare enrolmentPropsToImport: StatefulArray<number>;

  // financeSalutation specifies the Salutation for a learner's Finance
  // Account.  Only applied if updateFinanceSalutation is true.
  @attr('string', { defaultValue: '' })
  declare financeSalutation: string;

  // updateFinanceSalutation specifies whether to update the account
  // salutation, so that existing salutations don't need to be provided
  // in order to prevent them from being cleared.
  @attr('boolean', { defaultValue: false })
  declare updateFinanceSalutation: boolean;
}
